function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { dropUndefinedKeys, arrayify } from '@sentry/utils';
import { getDynamicSamplingContextFromSpan } from '../tracing/dynamicSamplingContext.js';
import { getRootSpan } from './getRootSpan.js';
import { spanToTraceContext, spanToJSON } from './spanUtils.js';
/**
 * Applies data from the scope to the event and runs all event processors on it.
 */

function applyScopeDataToEvent(event, data) {
  var fingerprint = data.fingerprint,
      span = data.span,
      breadcrumbs = data.breadcrumbs,
      sdkProcessingMetadata = data.sdkProcessingMetadata; // Apply general data

  applyDataToEvent(event, data); // We want to set the trace context for normal events only if there isn't already
  // a trace context on the event. There is a product feature in place where we link
  // errors with transaction and it relies on that.

  if (span) {
    applySpanToEvent(event, span);
  }

  applyFingerprintToEvent(event, fingerprint);
  applyBreadcrumbsToEvent(event, breadcrumbs);
  applySdkMetadataToEvent(event, sdkProcessingMetadata);
}
/** Merge data of two scopes together. */


function mergeScopeData(data, mergeData) {
  var extra = mergeData.extra,
      tags = mergeData.tags,
      user = mergeData.user,
      contexts = mergeData.contexts,
      level = mergeData.level,
      sdkProcessingMetadata = mergeData.sdkProcessingMetadata,
      breadcrumbs = mergeData.breadcrumbs,
      fingerprint = mergeData.fingerprint,
      eventProcessors = mergeData.eventProcessors,
      attachments = mergeData.attachments,
      propagationContext = mergeData.propagationContext,
      transactionName = mergeData.transactionName,
      span = mergeData.span;
  mergeAndOverwriteScopeData(data, 'extra', extra);
  mergeAndOverwriteScopeData(data, 'tags', tags);
  mergeAndOverwriteScopeData(data, 'user', user);
  mergeAndOverwriteScopeData(data, 'contexts', contexts);
  mergeAndOverwriteScopeData(data, 'sdkProcessingMetadata', sdkProcessingMetadata);

  if (level) {
    data.level = level;
  }

  if (transactionName) {
    // eslint-disable-next-line deprecation/deprecation
    data.transactionName = transactionName;
  }

  if (span) {
    data.span = span;
  }

  if (breadcrumbs.length) {
    data.breadcrumbs = [].concat(_toConsumableArray(data.breadcrumbs), _toConsumableArray(breadcrumbs));
  }

  if (fingerprint.length) {
    data.fingerprint = [].concat(_toConsumableArray(data.fingerprint), _toConsumableArray(fingerprint));
  }

  if (eventProcessors.length) {
    data.eventProcessors = [].concat(_toConsumableArray(data.eventProcessors), _toConsumableArray(eventProcessors));
  }

  if (attachments.length) {
    data.attachments = [].concat(_toConsumableArray(data.attachments), _toConsumableArray(attachments));
  }

  data.propagationContext = _objectSpread(_objectSpread({}, data.propagationContext), propagationContext);
}
/**
 * Merges certain scope data. Undefined values will overwrite any existing values.
 * Exported only for tests.
 */


function mergeAndOverwriteScopeData(data, prop, mergeVal) {
  if (mergeVal && Object.keys(mergeVal).length) {
    // Clone object
    data[prop] = _objectSpread({}, data[prop]);

    for (var key in mergeVal) {
      if (Object.prototype.hasOwnProperty.call(mergeVal, key)) {
        data[prop][key] = mergeVal[key];
      }
    }
  }
}

function applyDataToEvent(event, data) {
  var extra = data.extra,
      tags = data.tags,
      user = data.user,
      contexts = data.contexts,
      level = data.level,
      transactionName = data.transactionName;
  var cleanedExtra = dropUndefinedKeys(extra);

  if (cleanedExtra && Object.keys(cleanedExtra).length) {
    event.extra = _objectSpread(_objectSpread({}, cleanedExtra), event.extra);
  }

  var cleanedTags = dropUndefinedKeys(tags);

  if (cleanedTags && Object.keys(cleanedTags).length) {
    event.tags = _objectSpread(_objectSpread({}, cleanedTags), event.tags);
  }

  var cleanedUser = dropUndefinedKeys(user);

  if (cleanedUser && Object.keys(cleanedUser).length) {
    event.user = _objectSpread(_objectSpread({}, cleanedUser), event.user);
  }

  var cleanedContexts = dropUndefinedKeys(contexts);

  if (cleanedContexts && Object.keys(cleanedContexts).length) {
    event.contexts = _objectSpread(_objectSpread({}, cleanedContexts), event.contexts);
  }

  if (level) {
    event.level = level;
  }

  if (transactionName) {
    event.transaction = transactionName;
  }
}

function applyBreadcrumbsToEvent(event, breadcrumbs) {
  var mergedBreadcrumbs = [].concat(_toConsumableArray(event.breadcrumbs || []), _toConsumableArray(breadcrumbs));
  event.breadcrumbs = mergedBreadcrumbs.length ? mergedBreadcrumbs : undefined;
}

function applySdkMetadataToEvent(event, sdkProcessingMetadata) {
  event.sdkProcessingMetadata = _objectSpread(_objectSpread({}, event.sdkProcessingMetadata), sdkProcessingMetadata);
}

function applySpanToEvent(event, span) {
  event.contexts = _objectSpread({
    trace: spanToTraceContext(span)
  }, event.contexts);
  var rootSpan = getRootSpan(span);

  if (rootSpan) {
    event.sdkProcessingMetadata = _objectSpread({
      dynamicSamplingContext: getDynamicSamplingContextFromSpan(span)
    }, event.sdkProcessingMetadata);
    var transactionName = spanToJSON(rootSpan).description;

    if (transactionName) {
      event.tags = _objectSpread({
        transaction: transactionName
      }, event.tags);
    }
  }
}
/**
 * Applies fingerprint from the scope to the event if there's one,
 * uses message if there's one instead or get rid of empty fingerprint
 */


function applyFingerprintToEvent(event, fingerprint) {
  // Make sure it's an array first and we actually have something in place
  event.fingerprint = event.fingerprint ? arrayify(event.fingerprint) : []; // If we have something on the scope, then merge it with event

  if (fingerprint) {
    event.fingerprint = event.fingerprint.concat(fingerprint);
  } // If we have no data at all, remove empty array default


  if (event.fingerprint && !event.fingerprint.length) {
    delete event.fingerprint;
  }
}

export { applyScopeDataToEvent, mergeAndOverwriteScopeData, mergeScopeData };